import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import { fetchRankings } from "lib/fetchers/rankings";
import { fetchNavbarAndFooter } from "lib/fetchers/common";
import type { RankingData } from "lib/fetchers/rankings";

import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import RankingsView from "../../content-ui/rankings/rankingsView";

export async function getServerSideProps({ res, preview = false }) {
  const [menuItems, pageData] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchRankings({ preview }),
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  return {
    props: {
      rankings: pageData?.rankings,
      previewMode: preview,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type RankingsPageProps = {
  rankings: RankingData[];
  previewMode: boolean;
};

const RankingsPage = (props: RankingsPageProps) => {
  const title = "Top European startups to follow | Sifted";
  const description =
    "Dive in for our take on the most exciting startups in Europe. Sorted by country and sector.";
  const seoImage =
    "https://images.sifted.eu/wp-content/uploads/2023/05/11175026/Rankings.jpg?auto=format,compress&q=75&w=1200";

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: buildCanonicalUrl("/rankings"),
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      {props.previewMode ? <PreviewModeBanner exitSlug={`/rankings`} /> : null}
      <ErrorBoundary>
        <RankingsView rankings={props.rankings} />
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default RankingsPage;
